/**
 * Centralized permission definitions for the application.
 *
 * All role-based access control logic should derive from this module
 * so that permission rules live in a single place and are never
 * duplicated as magic strings across components.
 */

// ---------------------------------------------------------------------------
// Role constants
// ---------------------------------------------------------------------------

export const USER_TYPES = {
  ADMIN: "ADMIN",
  STAFF: "STAFF",
  GMAIL_MANAGER: "GMAIL_MANAGER",
  PORTFOLIO_MANAGER: "PORTFOLIO_MANAGER",
  CUSTOMER: "CUSTOMER",
} as const;

/** Union of all valid user-type strings. */
export type UserType = (typeof USER_TYPES)[keyof typeof USER_TYPES];

// ---------------------------------------------------------------------------
// Role groups
// ---------------------------------------------------------------------------

/** Roles with access to the admin panel (/admin and sub-routes). */
export const ADMIN_ROLES: UserType[] = [
  USER_TYPES.ADMIN,
  USER_TYPES.STAFF,
  USER_TYPES.GMAIL_MANAGER,
  USER_TYPES.PORTFOLIO_MANAGER,
];

/** Roles with access to the customer panel (/home and sub-routes). */
export const CUSTOMER_ROLES: UserType[] = [USER_TYPES.CUSTOMER];

// ---------------------------------------------------------------------------
// Route-access matrix
// ---------------------------------------------------------------------------

/**
 * Maps each user type to the route prefixes it may visit.
 * Use `"*"` to indicate unrestricted access (ADMIN only).
 *
 * Keep this in sync with the route definitions in src/routes/index.tsx.
 */
export const ROLE_ROUTE_ACCESS: Record<UserType, string[]> = {
  [USER_TYPES.ADMIN]: ["*"],
  [USER_TYPES.STAFF]: [
    "/cases",
    "/notificacoes",
    "/admin",
    "/profile-quality",
    "/backlinks-dashboard",
    "/change-password",
    "/novidades",
  ],
  [USER_TYPES.GMAIL_MANAGER]: [
    "/cases",
    "/following-profiles",
    "/profile-quality",
    "/clients",
    "/email",
    "/admin",
    "/backlinks-dashboard",
    "/change-password",
    "/novidades",
  ],
  [USER_TYPES.PORTFOLIO_MANAGER]: [
    "/clients",
    "/notificacoes",
    "/following-profiles",
    "/profile-quality",
    "/cases",
    "/admin",
    "/backlinks-dashboard",
    "/change-password",
    "/novidades",
  ],
  [USER_TYPES.CUSTOMER]: [
    "/home",
    "/profiles-tracking",
    "/reviews",
    "/posts",
    "/reports",
    "/select-profile",
    "/backlinks-dashboard",
    "/change-password",
  ],
};

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

/**
 * Returns true when `userType` belongs to the admin-panel group
 * (ADMIN, STAFF, GMAIL_MANAGER, PORTFOLIO_MANAGER).
 */
export function isAdminRole(userType: string): boolean {
  return ADMIN_ROLES.includes(userType as UserType);
}

/**
 * Returns true when `userType` belongs to the customer-panel group
 * (CUSTOMER).
 */
export function isCustomerRole(userType: string): boolean {
  return CUSTOMER_ROLES.includes(userType as UserType);
}

/**
 * Returns true when the given `userType` may navigate to `path`.
 *
 * For roles with wildcard (`"*"`) access every path is allowed.
 * For all other roles the path must start with one of the configured prefixes.
 */
export function hasRouteAccess(userType: string, path: string): boolean {
  const routes = ROLE_ROUTE_ACCESS[userType as UserType];
  if (!routes) return false;
  if (routes.includes("*")) return true;
  return routes.some((prefix) => path.startsWith(prefix));
}

/**
 * Narrows a plain `string` to the `UserType` union.
 * Useful when reading `userType` from external sources (API, localStorage).
 */
export function isValidUserType(userType: string): userType is UserType {
  return Object.values(USER_TYPES).includes(userType as UserType);
}
